import { z } from 'zod';

/**
 * Casas decimais de EXIBICAO da empresa (Empresa > Parametros > Exibicao). So apresentacao: o banco e o motor seguem na
 * precisao propria (6 casas de dinheiro/quantidade, 8 de taxa). Arredondamento HALF_UP (o ROUND do Excel: empate vai para
 * longe do zero). Sem vigencia: vale o valor gravado agora, com auditoria.
 */
export const CASAS_MIN = 0;
export const CASAS_MAX = 10;

export const CASAS_PADRAO = { casasDinheiro: 2, casasPercentual: 2, casasMarkup: 6, casasCusto: 6 } as const;

export const TIPOS_CASAS = ['dinheiro', 'percentual', 'markup', 'custo'] as const;
/** dinheiro = preco, total, lucro em R$; percentual = em pontos percentuais (21,85%); markup = fator; custo = custo unitario e CPM. */
export type TipoCasas = (typeof TIPOS_CASAS)[number];

const casas = (rotulo: string) =>
  z
    .number(`${rotulo}: informe um numero`)
    .int(`${rotulo}: use um numero inteiro`)
    .min(CASAS_MIN, `${rotulo}: minimo ${CASAS_MIN}`)
    .max(CASAS_MAX, `${rotulo}: maximo ${CASAS_MAX}`);

/** PUT /empresa/exibicao. Entrada = saida (sem transform). */
export const exibicaoEmpresaSchema = z.object({
  casasDinheiro: casas('Dinheiro'),
  casasPercentual: casas('Percentual'),
  casasMarkup: casas('Markup'),
  /** Custo unitario e CPM (custo por minuto). */
  casasCusto: casas('Custo e CPM'),
  /** Sessao 36: mostra o bloco "Reforma tributaria" na Formacao (so apresentacao). Ausente = mantem o gravado (padrao: ligado). */
  mostrarReformaFormacao: z.boolean().optional(),
});
export type ExibicaoEmpresaDto = z.infer<typeof exibicaoEmpresaSchema>;

export interface ExibicaoEmpresaResponse extends ExibicaoEmpresaDto {
  empresaId: string;
  mostrarReformaFormacao: boolean;
  /** false = a empresa ainda nao configurou (valem os padroes). */
  configurada: boolean;
  atualizadoEm: string | null;
}

/** Casas efetivas de um tipo, a partir da configuracao da empresa. */
export function casasDoTipo(c: ExibicaoEmpresaDto, tipo: TipoCasas): number {
  switch (tipo) {
    case 'dinheiro':
      return c.casasDinheiro;
    case 'percentual':
      return c.casasPercentual;
    case 'markup':
      return c.casasMarkup;
    case 'custo':
      return c.casasCusto;
  }
}

const RE_DEC = /^(-?)(\d+)(?:\.(\d*))?$/;

/**
 * Arredonda uma string decimal para `casas` casas, HALF_UP (empate para longe do zero, como o ROUND do Excel), sem
 * Number: a conta e feita em BigInt sobre os digitos, entao strings enormes nao perdem precisao. Devolve sempre a string
 * com exatamente `casas` casas ("1.005" com 2 vira "1.01"; "-2.5" com 0 vira "-3"). Zero negativo vira "0".
 * Invalido: null. A API usa a versao decimal.js (`apps/api/src/common/casas.ts`); um teste cruza as duas.
 */
export function arredondarHalfUp(valor: string, casas: number): string | null {
  const m = RE_DEC.exec(valor.trim());
  if (!m || !Number.isInteger(casas) || casas < 0) return null;
  const neg = m[1] === '-';
  const inteiro = m[2] ?? '0';
  const frac = m[3] ?? '';
  let digitos: bigint;
  if (frac.length <= casas) {
    digitos = BigInt(inteiro + frac.padEnd(casas, '0'));
  } else {
    const corte = frac.slice(0, casas);
    digitos = BigInt(inteiro + corte);
    if ((frac.charCodeAt(casas) ?? 48) >= 53) digitos += 1n;
  }
  let s = digitos.toString().padStart(casas + 1, '0');
  const pos = s.length - casas;
  s = casas === 0 ? s : `${s.slice(0, pos)}.${s.slice(pos)}`;
  return neg && digitos !== 0n ? `-${s}` : s;
}
